import React, { useMemo } from "react"
import { LayerGroup, useMap } from "react-leaflet"

import RoutePoints from "@/features/route/RoutePoints"
import RouteLines from "@/features/route/RouteLines"
import { useViewportWindow } from "@/hooks/useViewportWindow"
import { filterAlbisFromRoute } from "@/utils/RouteProcessing"

export default function RouteController({
  openPanels,
  mapBounds,
  vesselReportedRoutes,
  vesselPlanRoutes,
  switchReported,
  switchPlan,
  speedProfileData,
  consumptionProfileData,
  setRouteConsumption,
  setPathfinderResponse,
  setEtaError,
  switchVesselFiltersActive,
  openWaypointCard,
  openAlertCard,
  calculatorDepartureTime,
  calculatorBasespeed,
  setEtaResponse,
  plannerVesselSettings,
  cwViaAreasGeojson,
  applyRnwSafety,
  applyEca,
  avoidHra,
  avoidJwla,
  avoidCaa,
}) {
  const map = useMap()
  const { norm, viewportTick } = useViewportWindow(map)

  const filteredVesselReportedRoutes = useMemo(() => {
    if (!vesselReportedRoutes) return null
    return vesselReportedRoutes.map((route) => filterAlbisFromRoute(route))
  }, [vesselReportedRoutes])

  const layers = []

  const MemoizedPlanRoutes = useMemo(() => {
    if (switchPlan && vesselPlanRoutes && switchVesselFiltersActive) {
      return (
        <LayerGroup key={"planned"}>
          <RoutePoints
            vesselRoutes={vesselPlanRoutes}
            mapBounds={mapBounds}
            speedProfileData={speedProfileData}
            consumptionProfileData={consumptionProfileData}
            setRouteConsumption={setRouteConsumption}
            setPathfinderResponse={setPathfinderResponse}
            setEtaError={setEtaError}
            norm={norm}
            viewportTick={viewportTick}
            openWaypointCard={openWaypointCard}
            openAlertCard={openAlertCard}
            calculatorDepartureTime={calculatorDepartureTime}
            calculatorBasespeed={calculatorBasespeed}
            setEtaResponse={setEtaResponse}
            plannerVesselSettings={plannerVesselSettings}
            cwViaAreasGeojson={cwViaAreasGeojson}
            applyRnwSafety={applyRnwSafety}
            applyEca={applyEca}
            avoidHra={avoidHra}
            avoidJwla={avoidJwla}
            avoidCaa={avoidCaa}
          />
          <RouteLines
            vesselRoutes={vesselPlanRoutes}
            mapBounds={mapBounds}
            speedProfileData={speedProfileData}
            consumptionProfileData={consumptionProfileData}
            setRouteConsumption={setRouteConsumption}
            setPathfinderResponse={setPathfinderResponse}
            setEtaError={setEtaError}
            norm={norm}
            viewportTick={viewportTick}
            calculatorDepartureTime={calculatorDepartureTime}
            calculatorBasespeed={calculatorBasespeed}
            setEtaResponse={setEtaResponse}
            plannerVesselSettings={plannerVesselSettings}
            cwViaAreasGeojson={cwViaAreasGeojson}
            applyRnwSafety={applyRnwSafety}
            applyEca={applyEca}
            avoidHra={avoidHra}
            avoidJwla={avoidJwla}
            avoidCaa={avoidCaa}
          />
        </LayerGroup>
      )
    }
  }, [
    switchPlan,
    vesselReportedRoutes,
    mapBounds,
    vesselPlanRoutes,
    switchVesselFiltersActive,
    norm,
    viewportTick,
    calculatorDepartureTime,
    calculatorBasespeed,
    plannerVesselSettings,
    cwViaAreasGeojson,
    applyRnwSafety,
    applyEca,
    avoidHra,
    avoidJwla,
    avoidCaa,
  ])

  if (switchPlan && vesselPlanRoutes) {
    layers.push(MemoizedPlanRoutes)
  }

  const MemoizedReportRoutes = useMemo(() => {
    if (switchReported && filteredVesselReportedRoutes && switchVesselFiltersActive) {
      return (
        <LayerGroup key={"Report"}>
          <RoutePoints
            vesselRoutes={filteredVesselReportedRoutes}
            mapBounds={mapBounds}
            speedProfileData={speedProfileData}
            consumptionProfileData={consumptionProfileData}
            setRouteConsumption={setRouteConsumption}
            setPathfinderResponse={setPathfinderResponse}
            setEtaError={setEtaError}
            norm={norm}
            viewportTick={viewportTick}
            openWaypointCard={openWaypointCard}
            openAlertCard={openAlertCard}
            calculatorDepartureTime={calculatorDepartureTime}
            calculatorBasespeed={calculatorBasespeed}
            setEtaResponse={setEtaResponse}
            plannerVesselSettings={plannerVesselSettings}
            cwViaAreasGeojson={cwViaAreasGeojson}
            applyRnwSafety={applyRnwSafety}
            applyEca={applyEca}
            avoidHra={avoidHra}
            avoidJwla={avoidJwla}
            avoidCaa={avoidCaa}
          />
          <RouteLines
            vesselRoutes={filteredVesselReportedRoutes}
            mapBounds={mapBounds}
            speedProfileData={speedProfileData}
            consumptionProfileData={consumptionProfileData}
            setRouteConsumption={setRouteConsumption}
            setPathfinderResponse={setPathfinderResponse}
            setEtaError={setEtaError}
            norm={norm}
            viewportTick={viewportTick}
            calculatorDepartureTime={calculatorDepartureTime}
            calculatorBasespeed={calculatorBasespeed}
            setEtaResponse={setEtaResponse}
            plannerVesselSettings={plannerVesselSettings}
            cwViaAreasGeojson={cwViaAreasGeojson}
            applyRnwSafety={applyRnwSafety}
            applyEca={applyEca}
            avoidHra={avoidHra}
            avoidJwla={avoidJwla}
            avoidCaa={avoidCaa}
          />
        </LayerGroup>
      )
    }
  }, [
    switchReported,
    filteredVesselReportedRoutes,
    mapBounds,
    switchVesselFiltersActive,
    norm,
    viewportTick,
    calculatorDepartureTime,
    calculatorBasespeed,
    plannerVesselSettings,
    cwViaAreasGeojson,
    applyRnwSafety,
    applyEca,
    avoidHra,
    avoidJwla,
    avoidCaa,
  ])
  if (switchReported && filteredVesselReportedRoutes) {
    layers.push(MemoizedReportRoutes)
  }

  return <>{layers}</>
}
